/*
 * File: GetStudentPeriods.ts
 * Author: Austin Robinson
 * Date Created: 7/14/2024
 * Copyright (c) DrivingDoc 2023
 */
import { AxiosError } from 'axios';
import { createQuery } from 'react-query-kit';

import { ErrorResponse, ModifiedByUser, PostGreSQLInterval, privateApi } from 'api';

export type GetStudentPeriodsParams = {
  id: string;
};

export type StudentPeriodEnrollment = {
  tenant_id: string;
  student_user_id: string;
  course_period_id: string;
  course_enrollment_id: string;
  attendance: 'present' | 'absent' | 'no_permit' | null;
  attendance_taken_at: string | null;
  absence_fee: string | null;
  no_permit_fee: string | null;
  canceled_at: string | null;
  cancellation_fee: string | null;
  late_cancellation_fee: string | null;
  notes: string | null;
  private_notes: string | null;
  created_at: string | null;
  attendance_taken_by_user: ModifiedByUser | null;
  canceled_by_user: ModifiedByUser | null;
  created_by_user: ModifiedByUser | null;
  period_number: string | null;
  course_period: {
    tenant_id: string;
    id: string;
    course_section_id: string;
    start: string;
    end: string;
    open_enrollment: string | null;
    close_enrollment: string | null;
    capacity: number | null;
    deleted: boolean;
    created_at: string | null;
    updated_at: string | null;
    instructors: {
      id: string;
      title: string | null;
      nickname: string | null;
      first_name: string | null;
      last_name: string | null;
      formal_name: string | null;
    }[];
    course_section: {
      tenant_id: string;
      id: string;
      course_id: string;
      title: string;
      description: string | null;
      open_enrollment: string | null;
      close_enrollment: string | null;
      capacity: number | null;
      tenant_location_id: string | null;
      course: {
        tenant_id: string;
        id: string;
        course_type: 'behind_the_wheel' | 'in_person' | 'synchronous_online' | 'fully_online';
        schedule_type: 'self_scheduled' | 'fixed' | 'manual' | 'no_schedule';
        title: string;
        description: string | null;
        created_at: string | null;
        created_by: string | null;
        updated_at: string | null;
        updated_by: string | null;
      };
      tenant_location: {
        id: string;
        name: string | null;
        street_address: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
        phone: string | null;
        email: string | null;
      };
    };
  };
  course_enrollment: {
    tenant_id: string;
    id: string;
    student_user_id: string;
    course_section_id: string;
    student_charge_id: string | null;
    package_course_package_id: string | null;
    package_course_course_id: string | null;
    course_policy_course_id: string;
    course_policy_updated_at: string;
    course_enrollment_criteria_course_id: string;
    course_enrollment_criteria_updated_at: string;
    course_self_schedule_criteria_course_id: string | null;
    course_self_schedule_criteria_updated_at: string | null;
    course_completion_criteria_course_id: string;
    course_completion_criteria_updated_at: string;
    secure_seat_mode: 'at_time_of_enrollment' | 'when_deposit_paid' | 'when_full_cost_paid';
    seat_secured_at: boolean;
    enrolled_at: string;
    created_by: string | null;
    completed_at: string | null;
    tenant_location_id: string | null;
    performance_time_included: number | null;
    observation_time_included: number | null;
    requisite_group: number;
    became_eligible_at: string | null;
    course_policy: {
      tenant_id: string;
      course_id: string;
      updated_at: string;
      min_time_to_complete: PostGreSQLInterval | null;
      max_time_to_complete: PostGreSQLInterval | null;
      allow_self_service_extensions: boolean | null;
      standard_extension_time: PostGreSQLInterval | null;
      standard_extension_fee: number | null;
      max_extensions: number | null;
      max_time_to_request_extension: PostGreSQLInterval | null;
      absences_allowed_before_fee: number | null;
      absence_fee: number | null;
      absence_fee_due: PostGreSQLInterval | null;
      absence_fee_due_lock: PostGreSQLInterval | null;
      no_permits_allowed_before_fee: number | null;
      no_permit_fee: number | null;
      no_permit_fee_due: PostGreSQLInterval | null;
      no_permit_fee_due_lock: PostGreSQLInterval | null;
      allow_cancellations: boolean | null;
      cancellations_allowed_before_fee: number | null;
      cancellation_fee: number | null;
      cancellation_fee_due: PostGreSQLInterval | null;
      cancellation_fee_due_lock: PostGreSQLInterval | null;
      late_cancellations_allowed_before_fee: number | null;
      late_cancellation_fee: number | null;
      late_cancellation_fee_due: PostGreSQLInterval | null;
      late_cancellation_fee_due_lock: PostGreSQLInterval | null;
      late_cancellation_deadline: PostGreSQLInterval | null;
    };
  };
};

async function getStudentPeriodEnrollments(params: GetStudentPeriodsParams) {
  if (!params.id) {
    return null;
  }
  // make api call
  const result = await privateApi.get(`/users/students/${params.id}/schedule`, {});

  return result.data as StudentPeriodEnrollment[];
}

const useGetStudentPeriodEnrollments = createQuery<
  StudentPeriodEnrollment[] | null,
  GetStudentPeriodsParams,
  AxiosError<ErrorResponse>
>({
  queryKey: ['student_course_periods'],
  fetcher: (variables) => getStudentPeriodEnrollments(variables),
});

export { useGetStudentPeriodEnrollments };
